import { ReconTradeRow, BreakReasonItem } from "../../../Types/Reconautomation2.types";

export type ExportTab = "matched" | "mismatch" | "unmatched_bgm" | "unmatched_acers" | "failures" | "all" | "all_acers" | "all_bgm";

interface ExportColumn<T> {
  header: string;
  accessor: (row: T) => string | number | null | undefined;
}

const escapeCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  // Wrap in quotes if contains comma, quote, or newline
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCSV = <T>(columns: ExportColumn<T>[], rows: T[]): string => {
  const header = columns.map((c) => escapeCell(c.header)).join(",");
  const body = rows
    .map((row) => columns.map((c) => escapeCell(c.accessor(row))).join(","))
    .join("\n");
  return `${header}\n${body}`;
};

const triggerDownload = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatBreakReason = (value: ReconTradeRow["break_reason"]): string => {
  if (!value) return "";
  if (Array.isArray(value)) {
    return (value as unknown[])
      .map((item) => {
        if (typeof item === "object" && item !== null) {
          return (item as BreakReasonItem).reason ?? JSON.stringify(item);
        }
        return String(item);
      })
      .join("; ");
  }
  return String(value);
};

const formatBreakReasonRows = (value: ReconTradeRow["break_reason"]): string => {
  if (!value || !Array.isArray(value)) return "";
  const items = value as unknown[];
  const rows = Array.from(
    new Set(
      items
        .filter((item): item is BreakReasonItem => typeof item === "object" && item !== null && "row" in item)
        .map((item) => item.row)
    )
  );
  return rows.join(", ");
};

const formatCanonicalisationStatus = (status?: string | null): string => {
  if (!status) return "";
  return status
    .split(",")
    .map((s) => s.trim())
    .join("; ");
};

// ─── Column Definitions per Tab ────────────────────────────────────────────

const BASE_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  { header: "Trade Date", accessor: (r) => r.trade_date },
  { header: "Trade ID", accessor: (r) => r.source_trade_id },
  { header: "Client", accessor: (r) => r.client },
  { header: "Contract", accessor: (r) => r.contract },
  { header: "Quantity", accessor: (r) => r.quantity },
];

const ALL_COMMON_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  { header: "Trade Date", accessor: (r) => r.trade_date },
  { header: "Client", accessor: (r) => r.client },
  { header: "Contract", accessor: (r) => r.contract },
  { header: "Contract Month", accessor: (r) => r.contract_month },
  { header: "Direction", accessor: (r) => r.direction },
  { header: "Quantity", accessor: (r) => r.quantity },
  { header: "Price", accessor: (r) => r.price },
  { header: "Price (BGM)", accessor: (r) => r.bgm_price },
  { header: "Price (ACERS)", accessor: (r) => r.acers_price },
  { header: "Status", accessor: (r) => r.reconciliation_status },
  { header: "Break Reason", accessor: (r) => formatBreakReason(r.break_reason) },
];

const ALL_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  { header: "Trade ID", accessor: (r) => r.source_trade_id },
  { header: "Matched With ID", accessor: (r) => r.matched_with_id },
  ...ALL_COMMON_COLUMNS,
];


const ALL_ACERS_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  { header: "Trade ID", accessor: (r) => r.source_trade_id },
  { header: "Matched With ID", accessor: (r) => r.matched_with_id },
  ...ALL_COMMON_COLUMNS,
];

const ALL_BGM_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  { header: "Matched With ID", accessor: (r) => r.matched_with_id },
  { header: "Source Trade ID", accessor: (r) => r.source_trade_id },
  ...ALL_COMMON_COLUMNS,
];

const MATCHED_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  ...BASE_COLUMNS,
  { header: "Price (BGM)", accessor: (r) => r.bgm_price },
  { header: "Price (ACERS)", accessor: (r) => r.acers_price },
  { header: "Status", accessor: (r) => r.reconciliation_status },
];

const MISMATCH_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  ...BASE_COLUMNS,
  { header: "Price (BGM)", accessor: (r) => r.bgm_price },
  { header: "Price (ACERS)", accessor: (r) => r.acers_price },
  { header: "Markup (BGM)", accessor: (r) => r.markup_bgm },
  { header: "Markup (ACERS)", accessor: (r) => r.markup_acers },
  { header: "Status", accessor: (r) => r.reconciliation_status },
  { header: "Break Reason", accessor: (r) => formatBreakReason(r.break_reason) },
];

const UNMATCHED_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  ...BASE_COLUMNS,
  { header: "Contract Month", accessor: (r) => r.contract_month },
  { header: "Status", accessor: (r) => r.reconciliation_status },
  { header: "Break Reason", accessor: (r) => formatBreakReason(r.break_reason) },
];

const FAILURES_COLUMNS: ExportColumn<ReconTradeRow>[] = [
  { header: "Trade Date", accessor: (r) => r.trade_date },
  { header: "Trade ID", accessor: (r) => r.source_trade_id },
  { header: "Client", accessor: (r) => r.client },
  { header: "Contract", accessor: (r) => r.contract },
  { header: "Quantity", accessor: (r) => r.quantity },
  { header: "Contract Month", accessor: (r) => r.contract_month },
  { header: "Direction", accessor: (r) => r.direction },
  { header: "Uploaded File Row", accessor: (r) => formatBreakReasonRows(r.break_reason) },
  { header: "Source", accessor: (r) => r.source_system },
  { header: "Canon Status", accessor: (r) => formatCanonicalisationStatus(r.canonicalisation_status) },
  { header: "Issue", accessor: (r) => formatBreakReason(r.break_reason) },
];

// ─── Column Map ────────────────────────────────────────────────────────────

const COLUMNS_BY_TAB: Record<ExportTab, ExportColumn<ReconTradeRow>[]> = {
  all: ALL_COLUMNS,
  all_acers: ALL_ACERS_COLUMNS,
  all_bgm: ALL_BGM_COLUMNS,
  matched: MATCHED_COLUMNS,
  mismatch: MISMATCH_COLUMNS,
  unmatched_bgm: UNMATCHED_COLUMNS,
  unmatched_acers: UNMATCHED_COLUMNS,
  failures: FAILURES_COLUMNS,
};

const FILENAME_BY_TAB: Record<ExportTab, string> = {
  all: "recon_all",
  all_acers: "recon_all_acers",
  all_bgm: "recon_all_bgm",
  matched: "recon_matched",
  mismatch: "recon_mismatches",
  unmatched_bgm: "recon_unmatched_bgm",
  unmatched_acers: "recon_unmatched_acers",
  failures: "recon_failures",
};


export const exportReconCSV = (tab: ExportTab, rows: ReconTradeRow[], runId?: string | null): void => {
  if (!rows.length) return;

  const columns = COLUMNS_BY_TAB[tab];
  const csv = buildCSV(columns, rows);

  const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const suffix = runId ? `_${runId}` : "";
  const filename = `${FILENAME_BY_TAB[tab]}${suffix}_${timestamp}.csv`;

  triggerDownload(csv, filename);
};